import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSessionStore } from "./SessionStore"; // ✅ Zugriff auf den globalen Zustand
import './css/Header.css';
import { NavLink, Link, useNavigate } from "react-router-dom"; // Stelle sicher, dass NavLink importiert ist
import { Switch, FormControlLabel } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PasswordIcon from '@mui/icons-material/Password';
import HelpIcon from '@mui/icons-material/Help';
import LogoutIcon from '@mui/icons-material/Logout';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ConsentIcon from '@mui/icons-material/AssignmentTurnedIn';
import Tooltip from './components/Tooltip';

import { useAuth } from "./hooks/useAuth"; // Nutze den Auth-Context
import { useSiteSettings } from './context/SiteSettingsContext';
import { useUsageStatus } from "./hooks/useUsageStatus";


interface UserSettings {
  email: string;
  theme: string;
  notifications_enabled: boolean;
  title: string;
  language: string;
  is_admin_user: boolean;
  admin_role: string;
}
type HeaderProps = {
  showDevTools: boolean;
  toggleView: () => void;
};

const userMenuItems = [
  {
    label: "Profileinstellungen",
    to: "/settings",
    icon: <PersonIcon fontSize="small" style={{ color: "#32768d" }} />
  },
  {
    label: "Abonnement verwalten",
    to: "/settings/planpicker",
    icon: <SubscriptionsIcon fontSize="small" style={{ color: "#32768d" }} />
  },
  {
    label: "Passwort ändern",
    to: "/settings/changepassword",
    icon: <PasswordIcon fontSize="small" style={{ color: "#32768d" }} />
  },
  {
    label: "Dokumentationseinstellungen",
    to: "/settings/documentationsettings",
    icon: <NoteAltIcon fontSize="small" style={{ color: "#32768d" }} />
  },
  {
    label: "Klienteneinwilligung downloaden",
    to: "/settings/clientconsent",
    icon: <ConsentIcon fontSize="small" style={{ color: "#32768d" }} />
  }
];

const Header: React.FC<HeaderProps> = ({ showDevTools, toggleView }) => {
  const { user, logout, openLoginDialog, openRegisterDialog, openChangePasswordDialog } = useAuth();
  const navigate = useNavigate();
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSmallDropdown, setShowSmallDropdown] = useState(false);

  const [userMenuTimeout, setUserMenuTimeout] = useState<NodeJS.Timeout | null>(null);
  const closeDropdown = () => setShowDropdown(false); // Hilfsmethode
  const { setIsSessionSelected, setSelectedSessionId } = useSessionStore(); // ✅ Funktion abrufen
  const { data: usageStatus, isLoading: isUsageLoading } = useUsageStatus();

  const isAdmin = !!(settings?.is_admin_user && settings?.admin_role);



  useEffect(() => {
    if (user) {
      axios.get('/auth/user/get-user-settings') // Endpunkt anpassen
        .then((response) => {
          setSettings(response.data); // Die Daten aus der Axios-Antwort setzen
        })
        .catch((error) => {
          if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Abrufen der Benutzerdaten:', error);
        });
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest('.profile-icon-section') &&
        !target.closest('.dropdown-menu')
      ) {
        setShowDropdown(false);
        setShowSmallDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="header-container lg:z-[203]" id="header">
      <div className="left-header">

        <div className="header__left-item">
          <div id="mobile-profile-menu"><div className="side__bar__menu-bars">

            <button
              type="button"
              id="profile-menu-button"
              aria-haspopup="menu"
              aria-expanded={showSmallDropdown ? "true" : "false"}
              className="profile-menu-btn tw-py-1"
              onClick={() => {
                setShowSmallDropdown((prev) => !prev);
                if (userMenuTimeout) clearTimeout(userMenuTimeout);
                if (!showSmallDropdown) {
                  const timeout = setTimeout(() => {
                    setShowSmallDropdown(false);
                  }, 8000); // 5 Sekunden
                  setUserMenuTimeout(timeout);
                }
              }}
            >
              <div className="header-icons profile-icon-section__right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line x1="4" y1="8" x2="20" y2="8" />
                  <line x1="4" y1="16" x2="20" y2="16" />
                </svg>
              </div>
            </button>

            {showSmallDropdown && (
              <div
                className="dropdown-menu new-dropdown"
                style={{
                  position: "absolute",
                  left: "0",
                  top: "60px",
                  zIndex: 91000,
                  minWidth: "max-content",
                }}
                onMouseEnter={() => {
                  if (userMenuTimeout) clearTimeout(userMenuTimeout);
                }}
                onMouseLeave={() => {
                  const timeout = setTimeout(() => {
                    setShowSmallDropdown(false);
                  }, 5000);
                  setUserMenuTimeout(timeout);
                }}
              >
                <div role="menu" className="tw-flex tw-flex-col tw-w-[20rem] tw-px-2 mt-2 mb-2">
                  {userMenuItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.to}
                      className="profile__dropdown__item mb-2"
                      onClick={() => setShowSmallDropdown(false)}
                    >
                      <div className="profile__dropdown__item__img">{item.icon}</div>
                      <p className="!tw-font-normal">{item.label}</p>
                    </Link>
                  ))}
                  {/* <div className="profile__dropdown__item">
                    <div className="profile__dropdown__item__img">
                      <img src="/icons/baa.svg" alt="Generate BAA" width="18" height="18" />
                    </div>
                    <p className="!tw-font-normal">Generate BAA</p>
                  </div>*/}
                  <hr className="border-gray-300 my-1 mx-4  " />
                  <a
                    href="https://lumanote.de/help/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="profile__dropdown__item mb-2"
                  >
                    <div className="profile__dropdown__item__img">
                      <HelpIcon fontSize="small" style={{ color: "#32768d" }} />
                    </div>
                    <p className="!tw-font-normal">Hilfe & Kontakt</p>
                  </a>
                  <div className="profile__dropdown__item mb-2" onClick={() => { setShowSmallDropdown(false); logout(); }}>
                    <div className="profile__dropdown__item__img">
                      <LogoutIcon fontSize="small" style={{ color: "#32768d" }} />
                    </div>
                    <p className="!tw-font-normal">Abmelden</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>

        </div>
        <Link to="/" className="undefined active-class">
          <div className="relative m-0">
            {/* Mobile Logo */}
            <img
              src="/logo_only.png"
              className="block lg:hidden h-[35px] w-[35px]"
              alt="Mobiles Logo"
            />

            {/* Großes Logo */}
            <img
              src="/kvita-logo.png"
              className="new-header-logo-image-style tw-cursor-pointer hidden lg:block "

              alt="Desktop Logo"
            />
          </div>

        </Link>

        {user && (
          <div className="left__headers">
            <div className="header-icons">
              <NavLink to="/" className={({ isActive }) => isActive ? "header-link active-class" : "header-link"}
                onClick={() => {
                  setSelectedSessionId(null);    // Zustand zurücksetzen
                  setIsSessionSelected(false);   // Session deaktivieren
                  console.log("Navigiere zu /case");
                }}>
                <div className="title right-side-titles">Sitzungen</div>
              </NavLink>
            </div>
            {isAdmin && (
              <>
                <div className="header-icons">
                  <NavLink to="/case" className={({ isActive }) => isActive ? "header-link active-class" : "header-link"}
                    onClick={() => {
                      setSelectedSessionId(null);    // Zustand zurücksetzen
                      setIsSessionSelected(false);   // Session deaktivieren
                      console.log("Navigiere zu /case");
                    }}>
                    <div className="title right-side-titles" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Berichte
                        <span style={{
                          backgroundColor: '#E7A555', // z.B. Tailwind yellow-300
                          color: '#ffffff',
                          fontSize: '0.55rem',
                          padding: '3px 6px',
                          borderRadius: '6px',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          lineHeight: 1
                        }}>
                          beta
                        </span>
                    </div>
                  </NavLink>
                </div>
              </>
            )}
            {(settings?.is_admin_user && settings?.admin_role) && (
              <div className="header-icons">
                <NavLink to="/templates" className={({ isActive }) => isActive ? "header-link active-class" : "header-link"}>
                  <div className="title right-side-titles">Templates</div>
                </NavLink>
              </div>
            )}
            {(settings?.is_admin_user && settings?.admin_role) && (
              <div className="header-icons">
                <NavLink to="/structured-templates" className={({ isActive }) => isActive ? "header-link active-class" : "header-link"}>
                  <div className="title right-side-titles">New Templates</div>
                </NavLink>
              </div>
            )}{ }
            {(!settings?.is_admin_user && !settings?.admin_role) && (
              <div className="header-icons">
                <div className="header-link disabled-link" style={{ cursor: 'not-allowed', opacity: 0.6 }}>
                  <Tooltip text="Bald verfügbar! - Erstellen Sie anpassbare Dokumentationsvorlagen für Ihre Bedürfnisse.">
                    <div className="title right-side-titles" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Templates
                      <span style={{
                        backgroundColor: '#eab54d', // z.B. Tailwind yellow-300
                        color: '#000000',
                        fontSize: '0.55rem',
                        padding: '3px 6px',
                        borderRadius: '6px',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        lineHeight: 1
                      }}>
                        bald
                      </span>
                    </div>
                  </Tooltip>
                </div>
              </div>
            )}{ }
            {!isAdmin && (
              <div className="header-icons">
                <NavLink to="/settings/documentationsettings" className={({ isActive }) => isActive ? "header-link active-class" : "header-link"}>
                  <div className="title right-side-titles">Einstellungen</div>
                </NavLink>
              </div>
            )}
            <button id="new-session-button" className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 _smButton_19rto_188 !tw-font-normal smButton tw-ml-6   " onClick={() => {
              setSelectedSessionId(null); // Sitzung zurücksetzen
              setIsSessionSelected(false);
              navigate("/");
            }} >
              <div className="_btnFlex_19rto_32">
                <div className="_btnAddon_19rto_56 _btnLeftAddon_19rto_61">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    xmlns="http://www.w3.org/2000/svg" className="tw-w-5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
                Neue Sitzung
              </div>
            </button>
            {isAdmin && (
              <button id="new-session-button" className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80  !tw-font-normal smButton -ml-5 !w-2 !pr-3 !bg-[#E7A555] " onClick={() => {
                setSelectedSessionId(null); // Sitzung zurücksetzen
                setIsSessionSelected(false);
                navigate("/case");
              }} >
                <div className="_btnFlex_19rto_32">
                  <div className="_btnAddon_19rto_56 _btnLeftAddon_19rto_61">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                      xmlns="http://www.w3.org/2000/svg" className="tw-w-1">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </div>
                </div>
              </button>
            )}
            {/* </p>*/}
          </div>
        )}
        <div className="new-custom-header-style c-header-nav">
          {(settings?.is_admin_user && settings?.admin_role) && (
            <FormControlLabel
              control={
                <Switch
                  checked={showDevTools}
                  onChange={toggleView}
                  color="primary"
                />
              }
              label={showDevTools ? "Dev-Tools" : "User View"}
              labelPlacement="start"
            />
          )}
          {!isUsageLoading && usageStatus && (
            <Tooltip text="Hier klicken, um Ihren aktuellen Plan und verfügbare Upgrades anzusehen">
              <Link
                to="/settings/planpicker"
                className="lg:tw-flex md:tw-space-x-2 tw-items-center side-nav-counter tw-flex tw-cursor-pointer hover:tw-opacity-80"
              >
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.4427 6.95642L13.7894 9.74309C13.2294 12.1498 12.1227 13.1231 10.0427 12.9231C9.70939 12.8964 9.34939 12.8364 8.96272 12.7431L7.84272 12.4764C5.06272 11.8164 4.20272 10.4431 4.85606 7.65643L5.50939 4.86309C5.64272 4.29642 5.80272 3.80309 6.00272 3.39642C6.78272 1.78309 8.10939 1.34976 10.3361 1.87642L11.4494 2.13642C14.2427 2.78976 15.0961 4.16976 14.4427 6.95642Z" stroke="#707070" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M10.0363 12.9251C9.62299 13.2051 9.10299 13.4384 8.46965 13.6451L7.41632 13.9918C4.76965 14.8451 3.37632 14.1318 2.51632 11.4851L1.66299 8.85177C0.809654 6.2051 1.51632 4.8051 4.16299 3.95177L5.21632 3.6051C5.48965 3.51844 5.74965 3.4451 5.99632 3.39844C5.79632 3.8051 5.63632 4.29844 5.50299 4.8651L4.84965 7.65844C4.19632 10.4451 5.05632 11.8184 7.83632 12.4784L8.95632 12.7451C9.34299 12.8384 9.70299 12.8984 10.0363 12.9251Z" stroke="#707070" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M8.42969 5.6875L11.663 6.5075" stroke="#707070" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M7.77344 8.26562L9.70677 8.75896" stroke="#707070" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
                <p className="tw-my-0 tw-text-nowrap tw-whitespace-nowrap">
                  {usageStatus.unlimited ? (
                    "Unbegrenzte Dokumentationen"
                  ) : usageStatus.plan_name?.toLowerCase().includes("free") ? (
                    `${usageStatus.daily_remaining} Dokumentationen heute übrig`
                  ) : (
                    `${usageStatus.monthly_remaining} Dokumentationen übrig`
                  )}
                </p>
              </Link>
            </Tooltip>
          )}

          <div className="profile_ tw-flex-shrink-0">

            {user ? (

              <div className="profile-icon-section">

                <button
                  type="button"
                  id="profile-menu-button"
                  aria-haspopup="menu"
                  aria-expanded={showDropdown ? "true" : "false"}
                  className="profile-menu-btn tw-py-1"
                  onClick={() => {
                    setShowDropdown((prev) => !prev);
                    if (userMenuTimeout) clearTimeout(userMenuTimeout);
                    if (!showDropdown) {
                      const timeout = setTimeout(() => {
                        setShowDropdown(false);
                      }, 8000); // 5 Sekunden
                      setUserMenuTimeout(timeout);
                    }
                  }}
                >
                  <div className="header-icons profile-icon-section__right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="profile-icon">{(settings?.email || user || "")
                      ? (settings?.email || user || "").charAt(0).toUpperCase()
                      : ""}</div>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line x1="4" y1="8" x2="20" y2="8" />
                      <line x1="4" y1="16" x2="20" y2="16" />
                    </svg>
                  </div>
                </button>

                {showDropdown && (
                  <div
                    className="dropdown-menu new-dropdown"
                    style={{
                      position: "absolute",
                      right: "0",
                      top: "60px",
                      zIndex: 91000,
                      minWidth: "max-content",
                    }}
                    onMouseEnter={() => {
                      if (userMenuTimeout) clearTimeout(userMenuTimeout);
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setShowDropdown(false);
                      }, 5000);
                      setUserMenuTimeout(timeout);
                    }}
                  >
                    <div role="menu" className="tw-flex tw-flex-col tw-w-[20rem] tw-px-2 mt-2 mb-2">
                      {userMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.to}
                          className="profile__dropdown__item mb-2"
                          onClick={() => setShowDropdown(false)}
                        >
                          <div className="profile__dropdown__item__img">{item.icon}</div>
                          <p className="!tw-font-normal">{item.label}</p>
                        </Link>
                      ))}
                      {/* <div className="profile__dropdown__item">
                        <div className="profile__dropdown__item__img">
                          <img src="/icons/baa.svg" alt="Generate BAA" width="18" height="18" />
                        </div>
                        <p className="!tw-font-normal">Generate BAA</p>
                      </div>*/}
                      <hr className="border-gray-300 my-2 mx-4 " />
                      <a
                        href="https://lumanote.de/help/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="profile__dropdown__item mb-2"
                      >
                        <div className="profile__dropdown__item__img">
                          <HelpIcon fontSize="small" style={{ color: "#32768d" }} />
                        </div>
                        <p className="!tw-font-normal">Hilfe & Kontakt</p>
                      </a>
                      <div className="profile__dropdown__item mb-2" onClick={() => logout()}>
                        <div className="profile__dropdown__item__img">
                          <LogoutIcon fontSize="small" style={{ color: "#32768d" }} />
                        </div>
                        <p className="!tw-font-normal">Abmelden</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button onClick={openRegisterDialog} className="register-btn">Registrieren</button>
                <button onClick={openLoginDialog} className="login-btn">Login</button>
              </>
            )}
          </div>
        </div>

        <div id="dashboardBanner" className="tw-absolute tw-right-0 tw-top-[4rem] sm:tw-top-[6rem] tw-max-w-[20rem]"></div>

      </div><button className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 _smButton_19rto_188 !tw-font-normal smButton  lg:hidden" onClick={() => {
        setSelectedSessionId(null); // Sitzung zurücksetzen
        setIsSessionSelected(false);
        navigate("/");
      }} >
        <div className="_btnFlex_19rto_32">
          <div className="_btnAddon_19rto_56 ">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              xmlns="http://www.w3.org/2000/svg" className="tw-w-1">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>

        </div>
      </button>
    </div>

  );
};

export default Header;
